import { useCallback, useMemo, useRef, useState } from 'react';

import { createExecuteConnectorQuery } from './executor';
import { useDataConnector } from './use-data-connector';

import type { ConnectorQueryEcho, ConnectorResource } from './types';

/**
 * Items for each hydration request.
 *
 * The echo carries the SERVER page size, which is small on purpose: a 9-item
 * server render keeps the HTML light. Replaying that same `limit` would page the
 * browser 9 items at a time, so 304 items would cost 33 requests, each one
 * re-rendering the gallery. The browser pages larger instead. A live probe
 * against Contentful returned 304 items in one request, so 100 is well inside
 * what the API allows.
 */
const HYDRATION_PAGE_SIZE = 100;

export type ConnectorHydration = {
	/** Items fetched so far, on top of the server-resolved page. */
	items: unknown[];
	/** True while a page is in flight. */
	isHydrating: boolean;
	/** True when every page has arrived, or when there is nothing to fetch. */
	done: boolean;
	/** True while a connector resource still holds pages the browser has not fetched. */
	hasUnfetchedItems: boolean;
	/** Set when a hydration request failed. Paging stops, and the caller must recover. */
	error: Error | null;
	/**
	 * Start fetching every remaining page. Safe to call more than once.
	 *
	 * Stable across renders. The gallery passes this into a component it uses as an
	 * element type, and a new identity there remounts the whole subtree.
	 */
	hydrateRest: () => void;
};

/**
 * Hydrates the items a connector data resource did not server-render, by
 * replaying the echoed query with `skip` advanced.
 *
 * The first replay starts after the server page, at `skip + limit` from the echo
 * variables. The seed identity is keyed on the query text and that offset, so a
 * parent re-render cannot reset the accumulated pages and collapse the gallery.
 *
 * Paging is off until something calls `hydrateRest`, so a visitor who never
 * clicks "Load more" costs no request. `hydrateRest` then walks every remaining
 * page, one request for each idle turn, and the items arrive progressively.
 * Pass `autoLoad` to start on load instead.
 */
/**
 * Where hydration starts, and whether anything is left to fetch.
 *
 * The offset counts the items the server actually returned, not the `limit` it
 * asked for. A short last page, or an echo with no `limit`, would otherwise send
 * the browser back over items already on screen.
 */
function resolveStart({
	echo,
	total,
	serverItemCount,
}: {
	echo: ConnectorQueryEcho | null;
	total: number | undefined;
	serverItemCount: number | undefined;
}): { firstSkip: number; hasRemainder: boolean } {
	if (!echo) {
		return { firstSkip: 0, hasRemainder: false };
	}
	const serverPageSize = serverItemCount ?? echo.variables.limit ?? 0;
	const firstSkip = (echo.variables.skip ?? 0) + serverPageSize;
	const hasRemainder = typeof total === 'number' ? firstSkip < total : true;
	return { firstSkip, hasRemainder };
}

/** The seed resource: no items of its own, plus the echo for the next page. */
function buildSeed(
	echo: ConnectorQueryEcho | null,
	firstSkip: number,
	hasRemainder: boolean,
): ConnectorResource {
	if (!echo || !hasRemainder) {
		return { items: [], query: null };
	}
	return {
		items: [],
		query: {
			query: echo.query,
			variables: { ...echo.variables, skip: firstSkip, limit: HYDRATION_PAGE_SIZE },
		},
	};
}

export const useConnectorHydration = (
	echo: ConnectorQueryEcho | null,
	{
		autoLoad = false,
		total,
		serverItemCount,
	}: { autoLoad?: boolean; total?: number; serverItemCount?: number } = {},
): ConnectorHydration => {
	const executeQuery = useMemo(() => createExecuteConnectorQuery(), []);
	const [isPaging, setIsPaging] = useState(autoLoad);

	const { firstSkip, hasRemainder } = resolveStart({ echo, total, serverItemCount });
	const seedKey = echo && hasRemainder ? `${echo.query}::${firstSkip}::${String(total)}` : '';
	const seedRef = useRef<{ key: string; value: ConnectorResource }>();
	if (!seedRef.current || seedRef.current.key !== seedKey) {
		seedRef.current = { key: seedKey, value: buildSeed(echo, firstSkip, hasRemainder) };
	}

	// `useDataConnector` walks the remaining pages by itself while `autoLoad` is
	// true, one page for each idle turn. So "load the rest" is a single flag flip.
	const { items, isLoading, error, done } = useDataConnector({
		initial: seedRef.current.value,
		executeQuery,
		autoLoad: isPaging,
	});

	// A ref, so `hydrateRest` keeps one identity for the life of the component.
	const canPageRef = useRef(false);
	canPageRef.current = Boolean(seedRef.current.value.query);
	const hydrateRest = useCallback(() => {
		if (!canPageRef.current) return;
		setIsPaging(true);
	}, []);

	// A failed page stops paging, so stop claiming that items are still coming.
	// Otherwise the caller waits for a reveal that never arrives.
	const hasUnfetchedItems = Boolean(seedRef.current.value.query) && !done && !error;

	return { items, isHydrating: isLoading, error, done, hasUnfetchedItems, hydrateRest };
};
